import { Shield } from 'lucide-react';
import { useTrading, useEngine } from '../context/MarketContext';
import type { EngineRegime } from '../types';
import { Panel } from '../components/ui';
import { colors } from '../styles/tokens';

const REGIME_COLORS: Record<EngineRegime, string> = {
  ACTIVE: colors.accent.green, QUIET: colors.accent.blue, DORMANT: colors.text.muted, VOLATILE: colors.accent.red,
  accumulation: colors.accent.purple, distribution: colors.accent.yellow, neutral: colors.text.muted,
};
const REGIME_LABEL: Record<EngineRegime, string> = {
  ACTIVE: 'ACTIVE', QUIET: 'QUIET', DORMANT: 'DORMANT', VOLATILE: 'VOLATILE',
  accumulation: 'ACCUM', distribution: 'DIST', neutral: 'NEUTRAL',
};

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return `${Math.floor(diff / 3_600_000)}h ago`;
}

function fmtTZS(n: number): string {
  if (n >= 1_000_000_000) return `TZS ${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `TZS ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `TZS ${(n / 1_000).toFixed(0)}K`;
  return `TZS ${n.toFixed(0)}`;
}

export default function RiskPage() {
  const { risk } = useTrading();
  const { activityFeed, riskEvents, settlementReport, regimeBySymbol } = useEngine();

  const riskFeedEvents = activityFeed.filter(e => e.kind === 'RISK_LIMIT');
  const netExposure = risk.longExposure - risk.shortExposure;

  const inv = risk.inventoryByTicker ?? {};
  const invEntries = Object.entries(inv);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Shield size={16} color="#7e8a96" />
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: colors.text.primary }}>Risk Monitor</h2>
      </div>

      {/* Zone 1 — 3×2 Risk Panels */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>

        {/* Panel 1 — Exposure */}
        <Panel title="Exposure">
          <MetricRow label="Gross" value={fmtTZS(risk.grossNotional)} />
          <MetricRow label="Long" value={fmtTZS(risk.longExposure)} color="#36c98e" />
          <MetricRow label="Short" value={fmtTZS(risk.shortExposure)} color="#ff5c5c" />
          <MetricRow
            label="Net"
            value={`${netExposure >= 0 ? '+' : ''}${fmtTZS(netExposure)}`}
            color={netExposure >= 0 ? colors.accent.green : colors.accent.red}
          />
          <MetricRow
            label="Capital Util"
            value={risk.capitalUtilization > 0 ? `${(risk.capitalUtilization * 100).toFixed(1)}%` : '—'}
            color={risk.capitalUtilization > 0.8 ? colors.accent.red : risk.capitalUtilization > 0.6 ? colors.accent.yellow : colors.accent.green}
          />
        </Panel>

        {/* Panel 2 — Inventory Risk */}
        <Panel title="Inventory risk" badge={invEntries.length > 0 ? invEntries.length : undefined}>
          {invEntries.length === 0 ? (
            <Placeholder />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ color: colors.text.muted, fontWeight: 700 }}>
                  <th style={{ textAlign: 'left', paddingBottom: 6 }}>SYM</th>
                  <th style={{ textAlign: 'right', paddingBottom: 6 }}>QTY</th>
                  <th style={{ textAlign: 'right', paddingBottom: 6 }}>UTIL</th>
                </tr>
              </thead>
              <tbody>
                {invEntries.map(([sym, d]) => (
                  <tr key={sym} style={{ borderTop: '1px solid #0a0d10' }}>
                    <td style={{ padding: '5px 0', color: colors.text.primary, fontWeight: 700 }}>{sym}</td>
                    <td style={{ textAlign: 'right', color: d.quantity >= 0 ? colors.accent.green : colors.accent.red }}>
                      {d.quantity.toLocaleString()}
                    </td>
                    <td style={{ textAlign: 'right', color: d.utilization > 0.8 ? colors.accent.red : d.utilization > 0.5 ? colors.accent.yellow : colors.text.secondary }}>
                      {(d.utilization * 100).toFixed(0)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        {/* Panel 3 — Price Risk */}
        <Panel title="Price risk">
          <MetricRow
            label="±1% Move"
            value={risk.priceSensitivity1pct > 0 ? fmtTZS(risk.priceSensitivity1pct) : '—'}
            color="#ff8c3a"
          />
          <MetricRow
            label="±5% Move"
            value={risk.priceSensitivity5pct > 0 ? fmtTZS(risk.priceSensitivity5pct) : '—'}
            color="#ff5c5c"
          />
          <MetricRow
            label="1σ Vol Risk"
            value={risk.realizedVolRisk > 0 ? fmtTZS(risk.realizedVolRisk) : '—'}
            color="#a78bfa"
          />
          {invEntries.length > 0 && (
            <>
              <div style={{ fontSize: 10, color: colors.text.muted, fontWeight: 700, marginTop: 10, marginBottom: 6 }}>PER TICKER</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '0 8px', fontSize: 10, color: colors.text.muted, fontWeight: 700, paddingBottom: 4 }}>
                <span>SYM</span><span>±1%</span><span>±5%</span><span>1σ</span>
              </div>
              {invEntries.map(([sym, d]) => (
                <div key={sym} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '0 8px', padding: '4px 0', borderTop: '1px solid #0a0d10' }}>
                  <span style={{ fontSize: 11, color: colors.text.secondary }}>{sym}</span>
                  <span style={{ fontSize: 11, color: colors.accent.yellow }}>{fmtTZS(d.price_sensitivity_1pct)}</span>
                  <span style={{ fontSize: 11, color: colors.accent.red }}>{fmtTZS(d.price_sensitivity_5pct)}</span>
                  <span style={{ fontSize: 11, color: colors.accent.purple }}>{d.vol_risk_1sigma > 0 ? fmtTZS(d.vol_risk_1sigma) : '—'}</span>
                </div>
              ))}
            </>
          )}
        </Panel>

        {/* Panel 4 — Liquidity Risk */}
        <Panel title="Liquidity risk">
          {invEntries.length === 0 ? (
            <Placeholder />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ color: colors.text.muted, fontWeight: 700 }}>
                  <th style={{ textAlign: 'left', paddingBottom: 6 }}>SYM</th>
                  <th style={{ textAlign: 'right', paddingBottom: 6 }}>EXIT %</th>
                </tr>
              </thead>
              <tbody>
                {invEntries.map(([sym, d]) => {
                  const exitPct = d.exit_ratio * 100;
                  return (
                    <tr key={sym} style={{ borderTop: '1px solid #0a0d10' }}>
                      <td style={{ padding: '5px 0', color: colors.text.primary, fontWeight: 700 }}>{sym}</td>
                      <td style={{
                        textAlign: 'right',
                        color: exitPct > 200 ? colors.accent.red : exitPct > 100 ? colors.accent.yellow : colors.accent.green,
                      }}>
                        {exitPct.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Panel>

        {/* Panel 5 — Capital Risk */}
        <Panel title="Capital risk">
          {invEntries.length === 0 ? (
            <Placeholder />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ color: colors.text.muted, fontWeight: 700 }}>
                  <th style={{ textAlign: 'left', paddingBottom: 6 }}>SYM</th>
                  <th style={{ textAlign: 'right', paddingBottom: 6 }}>ALLOC</th>
                  <th style={{ textAlign: 'right', paddingBottom: 6 }}>USED</th>
                </tr>
              </thead>
              <tbody>
                {invEntries.map(([sym, d]) => (
                  <tr key={sym} style={{ borderTop: '1px solid #0a0d10' }}>
                    <td style={{ padding: '5px 0', color: colors.text.primary, fontWeight: 700 }}>{sym}</td>
                    <td style={{ textAlign: 'right', color: colors.text.secondary }}>{fmtTZS(d.capital_allocated)}</td>
                    <td style={{
                      textAlign: 'right',
                      color: d.capital_used_pct > 0.8 ? colors.accent.red : d.capital_used_pct > 0.5 ? colors.accent.yellow : colors.accent.green,
                    }}>
                      {(d.capital_used_pct * 100).toFixed(0)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        {/* Panel 6 — Settlement */}
        <Panel title="Settlement">
          {settlementReport ? (
            <>
              <MetricRow label="Available Cash" value={fmtTZS(settlementReport.available_cash)} color="#36c98e" />
              <MetricRow label="Tradeable Cash" value={fmtTZS(settlementReport.tradeable_cash)} color="#4da3ff" />
              <MetricRow label="Reserved Cash" value={fmtTZS(settlementReport.reserved_cash)} color="#ff8c3a" />
              <MetricRow label="Committed Cash" value={fmtTZS(settlementReport.committed_cash)} color="#ff8c3a" />
              <MetricRow label="Receivables" value={fmtTZS(settlementReport.receivables)} />
              {settlementReport.settlement_dates.slice(0, 3).map((sd, i) => (
                <MetricRow
                  key={i}
                  label={`T+${i + 1}  (${sd.date.slice(5)})`}
                  value={sd.net >= 0 ? `+${fmtTZS(sd.net)}` : fmtTZS(sd.net)}
                  color={sd.net >= 0 ? colors.accent.green : colors.accent.red}
                />
              ))}
            </>
          ) : (
            <Placeholder />
          )}
        </Panel>
      </div>

      {/* Zone 2 — Regime Risk Adjustments */}
      <Panel title="Regime risk adjustments" badge={regimeBySymbol.size > 0 ? regimeBySymbol.size : undefined}>
        {regimeBySymbol.size === 0 ? (
          <div style={{ color: colors.text.muted, fontSize: 12 }}>No regime data — engine not connected</div>
        ) : (
          <>
            <div style={{
              display: 'grid', gridTemplateColumns: '80px 1fr 80px 80px 80px',
              gap: 8, padding: '6px 0', borderBottom: '1px solid #2a3540',
              fontSize: 10, fontWeight: 700, color: colors.text.muted, letterSpacing: '0.06em',
            }}>
              <div>SYMBOL</div>
              <div>REGIME</div>
              <div style={{ textAlign: 'right' }}>SPREAD ×</div>
              <div style={{ textAlign: 'right' }}>SIZE ×</div>
              <div style={{ textAlign: 'right' }}>CONF</div>
            </div>
            {Array.from(regimeBySymbol.entries()).map(([ticker, rs]) => {
              const rc = REGIME_COLORS[rs.regime as EngineRegime] ?? colors.text.muted;
              const rl = REGIME_LABEL[rs.regime as EngineRegime] ?? rs.regime;
              return (
                <div key={ticker} style={{
                  display: 'grid', gridTemplateColumns: '80px 1fr 80px 80px 80px',
                  gap: 8, padding: '8px 0', borderBottom: '1px solid #0a0d10', alignItems: 'center',
                }}>
                  <div style={{ fontWeight: 700, fontSize: 12, color: colors.text.primary }}>{ticker}</div>
                  <div>
                    <span style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                      backgroundColor: `${rc}22`, color: rc,
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                    }}>{rl}</span>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 12, color: colors.text.primary }}>
                    ×{(rs.spread_multiplier ?? 1).toFixed(1)}
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 12, color: colors.text.primary }}>
                    ×{(rs.size_multiplier ?? 1).toFixed(1)}
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 12, color: colors.text.secondary }}>
                    {Math.round(rs.confidence * 100)}%
                  </div>
                </div>
              );
            })}
          </>
        )}
      </Panel>

      {/* Zone 3 — Risk Event Log */}
      <Panel title="Risk event log">
        <div style={{ maxHeight: 220, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {riskFeedEvents.map(ev => (
            <div key={ev.id} style={{
              display: 'flex', gap: 12, padding: '8px 12px',
              borderLeft: '3px solid #ff5c5c', borderRadius: '0 6px 6px 0',
              background: colors.bg.primary, alignItems: 'flex-start',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: colors.text.primary }}>{ev.title}</div>
                {ev.detail && <div style={{ fontSize: 11, color: colors.text.secondary, marginTop: 2 }}>{ev.detail}</div>}
              </div>
              <div style={{ fontSize: 10, color: colors.text.muted, whiteSpace: 'nowrap' }}>{relTime(ev.timestamp)}</div>
            </div>
          ))}
          {riskEvents.map((re, i) => (
            <div key={`re-${i}`} style={{
              display: 'flex', gap: 12, padding: '8px 12px',
              borderLeft: '3px solid #ff5c5c', borderRadius: '0 6px 6px 0',
              background: colors.bg.primary, alignItems: 'flex-start',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: colors.text.primary }}>
                  {re.limit_type}{re.ticker ? ` · ${re.ticker}` : ''}: {re.action_taken}
                </div>
                <div style={{ fontSize: 11, color: colors.text.secondary, marginTop: 2 }}>
                  {re.current_value.toFixed(2)} / limit {re.limit_value.toFixed(2)}
                </div>
              </div>
              <div style={{ fontSize: 10, color: colors.text.muted, whiteSpace: 'nowrap' }}>{relTime(re.timestamp)}</div>
            </div>
          ))}
          {riskFeedEvents.length === 0 && riskEvents.length === 0 && (
            <div style={{ color: colors.text.muted, fontSize: 12, padding: '8px 0' }}>No risk events in this session</div>
          )}
        </div>
      </Panel>
    </div>
  );
}

function MetricRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '5px 0', borderBottom: '1px solid #0a0d10',
    }}>
      <span style={{ fontSize: 11, color: colors.text.secondary }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: color ?? colors.text.primary }}>{value}</span>
    </div>
  );
}

function Placeholder() {
  return <div style={{ fontSize: 11, color: colors.text.muted }}>No data</div>;
}
